import React, { useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types.ts"
import type { IOneVerifyNetworkStatusAlert } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { ROUTES_CONFIG } from "../../../config/routes.config.ts"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook.ts"
import { NetworkStatusRuleDetailPopupComponent } from "../../popups/networkStatusRuleDetailPopup.component"

import checkCircleOutlineIcon from "@front-shared/assets/images/icons/check-dark.svg"
import infoCircleIcon from "@front-shared/assets/images/icons/info-circle.svg"
import warningRedIcon from "@front-shared/assets/images/icons/warning-red.svg"

export const NetworkStatusAlertComponent: React.FunctionComponent<
    INetworkStatusAlertComponentProps
> = props => {
    const hasNetworkStatusFeatureFlag = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_NETWORK_STATUS_RULES,
    )

    const [ruleDetailsPopupOpen, setRuleDetailsPopupOpen] = useState(false)

    const openRulePopup = () => {
        setRuleDetailsPopupOpen(true)
    }

    const closeRulePopup = () => {
        setRuleDetailsPopupOpen(false)
    }

    if (!hasNetworkStatusFeatureFlag || !props.networkStatusAlert) {
        return null
    }

    const { isAlert, rule } = props.networkStatusAlert

    const linkStyle =
        "inline-block cursor-pointer underline text-brand-vibrantPurple text-semibold"

    return (
        <div
            className={`
                flex items-center gap-x-[8px]
                rounded-[4px] pt-[8px] px-[8px] pb-[6px]
                ${isAlert === true ? "bg-brand-warningYellow" : isAlert === false ? "bg-brand-warmLight" : "bg-brand-blueLight"}
                ${props.className}
            `}
        >
            <img
                className="h-[14px] w-[14px] shrink-0 relative -top-[1px]"
                src={
                    isAlert === true
                        ? warningRedIcon
                        : isAlert === false
                          ? checkCircleOutlineIcon
                          : infoCircleIcon
                }
                alt=""
            />

            <PrimaryText typography="caption">
                {isAlert === true && (
                    <>
                        <span className="text-brand-error text-bold">
                            Network Status Alert!
                        </span>{" "}
                        Below are the{" "}
                        {props.isInNetworkCheck
                            ? "in network"
                            : "out of network"}{" "}
                        benefits as requested, but Nirvana has identified this
                        may be{" "}
                        {props.isInNetworkCheck
                            ? "out of network"
                            : "in network"}{" "}
                        based on your organization's{" "}
                        <button type="button" onClick={openRulePopup}>
                            <span className={linkStyle}>
                                network status rules
                            </span>
                        </button>
                    </>
                )}

                {isAlert === false && (
                    <>
                        <span className="text-brand-primary text-bold">
                            Network Status Rule Matched
                        </span>{" "}
                        Below are the{" "}
                        {props.isInNetworkCheck
                            ? "in network"
                            : "out of network"}{" "}
                        benefits as requested, which correspond to your
                        organization's{" "}
                        <button type="button" onClick={openRulePopup}>
                            <span className={linkStyle}>
                                network status rules
                            </span>
                        </button>
                    </>
                )}

                {isAlert === null && (
                    <>
                        <span className="text-brand-primary text-bold">
                            No Network Status Rule Matched
                        </span>{" "}
                        If you would like to add a rule corresponding to the
                        details of this policy, you can do so in your
                        organization's{" "}
                        <a
                            href={ROUTES_CONFIG.networkStatusRulesUrl}
                            target="_blank"
                            rel="noopener"
                        >
                            <span className={linkStyle}>
                                network status rules
                            </span>
                        </a>
                    </>
                )}
            </PrimaryText>

            {(isAlert === true || isAlert === false) && (
                <NetworkStatusRuleDetailPopupComponent
                    isActive={ruleDetailsPopupOpen}
                    onDone={closeRulePopup}
                    rule={rule}
                    bluecardEligible={props.bluecardEligible}
                    requestPayer={props.requestPayer}
                    responsePayer={props.responsePayer}
                />
            )}
        </div>
    )
}

interface INetworkStatusAlertComponentProps {
    className?: string
    networkStatusAlert?: IOneVerifyNetworkStatusAlert | null
    isInNetworkCheck?: boolean
    bluecardEligible?: boolean | null
    requestPayer?: IPayer | null
    responsePayer?: IPayer | null
}
